import React, { useState } from 'react';
import { useLocation, useNavigate, Navigate, Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import SuccessToast from '../components/SuccessToast';
import ErrorToast from '../components/ErrorToast';
import { confirmPasswordReset, requestPasswordReset } from '../services/user-service';
import { useAuth } from '../context/AuthContext';

type LocationState = { email?: string; message?: string };

const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useAuth();

    const state = (location.state as LocationState) || {};
    const [email, setEmail] = useState(state.email ?? '');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState(state.message ?? '');
    const [errorToast, setErrorToast] = useState('');

    if (!isAuthenticated && !state.email) {
        return <Navigate to="/forgot-password" />;
    }

    const validate = (): boolean => {
        const newErrs: Record<string, string> = {};

        if (!email) newErrs.email = 'Email is required';
        if (email.length > 100) newErrs.email = 'Email must be 100 characters or fewer';

        if (!code) newErrs.code = 'Verification code is required';
        if (code.length > 20) newErrs.code = 'Verification code is too long';

        if (password !== confirmPassword) {
            newErrs.confirmPassword = 'Passwords do not match';
        } else if (password.length < 8 || password.length > 15) {
            newErrs.password = 'Password must be between 8 and 15 characters long';
        } else if (!/[a-z]/.test(password)) {
            newErrs.password = 'Password must include at least one lowercase letter';
        } else if (!/[A-Z]/.test(password)) {
            newErrs.password = 'Password must include at least one uppercase letter';
        } else if (!/[0-9]/.test(password)) {
            newErrs.password = 'Password must include at least one number';
        } else if (!/[^a-zA-Z0-9]/.test(password)) {
            newErrs.password = 'Password must include at least one special character';
        }

        setErrors(newErrs);
        return Object.keys(newErrs).length === 0;
    };

    const handleSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();
        setErrorToast('');
        setSuccessMessage('');

        if (!validate()) return;

        setLoading(true);
        try {
            const response = await confirmPasswordReset({ email, code, newPassword: password });
            if (response?.status === 204) {
                navigate('/login', {
                    state: { message: 'Your password has been reset. Please sign in with your new password.' },
                });
            } else if (response.status === 400) {
                const newErrs: Record<string, string> = { responseError: response.data as string };
                setErrors(newErrs);

            } else {
                console.error('Invalid response when resetting password', response);
                setErrorToast('Could not reset password. Check the code and try again.');
            }
        } catch (err: any) {
            console.error('Error resetting password', err);
            setErrorToast('Could not reset password. Check the code and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!email) {
            setErrors({ email: 'Enter your email first' });
            return;
        }
        setErrorToast('');
        setSuccessMessage('');
        setLoading(true);
        try {
            await requestPasswordReset(email);
            setSuccessMessage("If an account exists for that email, we've sent a new verification code.");
        } catch {
            setSuccessMessage("If an account exists for that email, we've sent a new verification code.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <NavBar />
            <div className="flex-grow mt-16">
                <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded shadow">
                    <h2 className="text-center text-2xl font-semibold text-crumbblue">Reset Password</h2>

                    {isAuthenticated && (
                        <p className="mt-2 text-xs text-gray-500 text-center" data-testid="reset-link">
                            Already signed in? For changing your password with your current one, use{' '}
                            <Link to="/change-password" className="text-crumbblue hover:underline">
                                Change Password
                            </Link>
                            .
                        </p>
                    )}

                    <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-crumbblue focus:outline-none"
                                data-testid="email-input"
                            />
                            {errors.email && (
                                <div className="text-red-600 text-sm mt-1" data-testid="email-error">
                                    {errors.email}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Verification code</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                required
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 tracking-widest focus:border-crumbblue focus:outline-none"
                                data-testid="code-input"
                            />
                            {errors.code && (
                                <div className="text-red-600 text-sm mt-1" data-testid="code-error">
                                    {errors.code}
                                </div>
                            )}

                            <div className="mt-3 flex items-center justify-between">
                                <span className="text-xs text-gray-500">Didn’t get a code?</span>
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={loading}
                                    aria-label="Resend verification code"
                                    className="mt-2 text-crumbblue hover:underline text-sm  disabled:opacity-60 font-semibold hover:bg-crumbblue/10"
                                    data-testid="resend-button"
                                >
                                    🔁 Resend code
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">New password</label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-crumbblue focus:outline-none"
                                data-testid="password-input"
                            />
                            {errors.password && (
                                <div className="text-red-600 text-sm mt-1" data-testid="password-error">
                                    {errors.password}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Confirm new password</label>
                            <input
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-crumbblue focus:outline-none"
                                data-testid="confirm-password-input"
                            />
                            {errors.confirmPassword && (
                                <div className="text-red-600 text-sm mt-1" data-testid="confirm-password-error">
                                    {errors.confirmPassword}
                                </div>
                            )}
                            {errors.responseError && (
                                <div className="text-red-600 text-sm mt-1" data-testid="response-error">
                                    {errors.responseError}
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-md bg-crumbblue p-2 font-semibold text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid="submit-button"
                        >
                            {loading ? 'Resetting...' : 'Reset password'}
                        </button>

                        <div className="text-center">
                            <Link to="/login" className="text-crumbblue hover:underline text-sm font-medium">
                                Back to login
                            </Link>
                        </div>
                    </form>

                    <SuccessToast message={successMessage} clearSuccess={() => setSuccessMessage('')} />
                    <ErrorToast message={errorToast} clearError={() => setErrorToast('')} />
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ResetPasswordPage;